import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../lib/api';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState(20); // Default large for seniors

  useEffect(() => {
    let mounted = true;

    // Failsafe: if INITIAL_SESSION never fires (e.g. Supabase init stall), unblock the UI.
    const failsafe = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 3000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (!session?.user || event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setFontSize(20);
          setLoading(false);
          return;
        }

        // Unblock the UI immediately — don't wait for the profile DB call.
        setUser(session.user);
        setLoading(false);

        // Fetch profile in background. On TOKEN_REFRESHED only retry if
        // profile is still missing (e.g. initial fetch failed).
        const needsProfile = event === 'INITIAL_SESSION' || event === 'SIGNED_IN';
        const missingProfile = event === 'TOKEN_REFRESHED'; // profile ref checked below
        if (needsProfile || missingProfile) {
          const fetchProfile = async (attempt = 0) => {
            try {
              const profileData = await auth.getProfile();
              if (!mounted) return;
              setProfile(profileData);
              setFontSize(profileData.tamanho_fonte || 20);
            } catch (e) {
              if (attempt < 2 && mounted) {
                setTimeout(() => fetchProfile(attempt + 1), 1500 * (attempt + 1));
              } else {
                console.error('Profile fetch failed after retries:', e);
              }
            }
          };
          // For TOKEN_REFRESHED, only fetch if profile isn't loaded yet.
          // Use a ref-free check via setProfile's functional form.
          if (needsProfile) {
            fetchProfile();
          } else {
            setProfile(prev => {
              if (!prev) fetchProfile();
              return prev;
            });
          }
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(failsafe);
      subscription.unsubscribe();
    };
  }, []);

  // Apply font size to content area only (via CSS var), not html root,
  // so Tailwind rem-based layout (button heights, paddings) stays stable.
  useEffect(() => {
    document.documentElement.style.setProperty('--app-font-size', `${fontSize}px`);
  }, [fontSize]);

  const loginWithGoogle = async () => {
    await auth.signInWithGoogle();
  };

  const login = async (email, senha) => {
    const { user: supabaseUser } = await auth.signIn(email, senha);
    const profileData = await auth.getProfile();
    setUser(supabaseUser);
    setProfile(profileData);
    setFontSize(profileData.tamanho_fonte || 20);
    return { id: supabaseUser.id, nome: profileData.nome, email: supabaseUser.email };
  };

  const register = async (nome, email, senha) => {
    const { user: supabaseUser } = await auth.signUp(email, senha, nome);
    // Profile is auto-created by the DB trigger
    setUser(supabaseUser);
    setProfile({ nome, tamanho_fonte: 20 });
    setFontSize(20);
    return { id: supabaseUser.id, nome, email };
  };

  const logout = useCallback(() => {
    // Clear local state immediately so the UI responds at once.
    setUser(null);
    setProfile(null);
    // Invalidate the session server-side in the background.
    auth.signOut().catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error('Logout error:', error);
      }
    });
  }, []);

  const increaseFontSize = async () => {
    const sizes = [16, 20, 24, 28];
    const currentIndex = sizes.indexOf(fontSize);
    if (currentIndex < sizes.length - 1) {
      const newSize = sizes[currentIndex + 1];
      setFontSize(newSize);

      if (user) {
        try {
          await auth.updateFontSize(newSize);
        } catch (error) {
          console.error('Failed to save font size:', error);
        }
      }
    }
  };

  const decreaseFontSize = async () => {
    const sizes = [16, 20, 24, 28];
    const currentIndex = sizes.indexOf(fontSize);
    if (currentIndex > 0) {
      const newSize = sizes[currentIndex - 1];
      setFontSize(newSize);

      if (user) {
        try {
          await auth.updateFontSize(newSize);
        } catch (error) {
          console.error('Failed to save font size:', error);
        }
      }
    }
  };

  const updateAvatar = async (avatar) => {
    const updated = await auth.updateAvatar(avatar);
    setProfile(prev => ({ ...prev, ...updated }));
  };

  const deleteAccount = async () => {
    try {
      await auth.deleteAccount();
    } catch (error) {
      // Ignore AbortError from auth lock; the DB delete already committed
      if (error?.name !== 'AbortError') throw error;
    }
    setUser(null);
    setProfile(null);
  };

  const value = {
    user: user ? {
      id: user.id,
      nome: profile?.nome,
      email: user.email,
      avatar: profile?.avatar || 'bird',
      tamanho_fonte: fontSize,
      plan: profile?.plan || 'gratuito',
      subscription_status: profile?.subscription_status || null,
      current_period_end: profile?.current_period_end || null,
      stripe_customer_id: profile?.stripe_customer_id || null,
    } : null,
    loading,
    fontSize,
    login,
    loginWithGoogle,
    register,
    logout,
    increaseFontSize,
    decreaseFontSize,
    updateAvatar,
    deleteAccount,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
